<script setup>
import { onMounted, computed } from "vue";
import AdditionalProduct from './AdditionalProduct.vue';
import MobileActionsDropdown from './MobileActionsDropdown.vue'
import SubProduct from './SubProduct.vue';
import Periods from './Periods.vue';
import ParameterBadge from './ParameterBadge.vue';
import WarningModal from '@/components/shared/WarningModal.vue'
import Button from '@/stories/Button.vue'
import IconInfoBlack from '@/components/checkout/components/icon/InfoBlack.vue'

import { getPriceLabel } from '@/utils/checkout';
import { useCheckoutStore } from "@/stores/checkout.store";
import { useGlobalStore } from "@/stores/global.store"
import { openModal } from "@/utils"
import { DotLottieVue } from '@lottiefiles/dotlottie-vue'

const props = defineProps({
  product: Object,
  isFirst: Boolean
})

const $checkout = useCheckoutStore();
const $global = useGlobalStore();

const isSubproduct = (product) => {
  if(product.category === 'vps_backup') {
    return false
  }
  if (
      (product.supported_periods && Object.keys(product.supported_periods).length > 1) ||
      product.category === 'select' ||
      product.update_count
    ) {
    return true
  }

  return false
}

const getItems = computed(() => {
  return {
    additionalProducts: props.product?.subitems?.filter(subitem => !isSubproduct(subitem)),
    subProducts: props.product?.subitems?.filter(subitem => isSubproduct(subitem)),
  };
})

onMounted(() => {
  window.webGlobe?.tooltip?.init()
})

const removeProduct = async () => {
  await $checkout.removeFromBasket({index: props.product.id, isFirst: props.isFirst, silent: true, name: props.product.update_name, pack: props.product.update_pack});
}

const supportedPeriods = computed(() => {
  if(!props.product.supported_periods) {
    return {}
  }
  const [_, domainTld] = props.product.name.split('.')
  return Object.values(props.product.supported_periods).reduce((periods, currentPeriod) => {
    const price = $global.getDomainPriceForPeriod(domainTld, currentPeriod.period)
    if (price === null) {
      return periods;
    }
    return {
      ...periods,
      [currentPeriod.period]: {
        final_price: price,
        period: currentPeriod.period,
        base_price: price
      }
    }
  }, {})
})

const isHandlingPeriods = computed(() => Object.keys(supportedPeriods.value).length > 1)

const inHandlingParameters = computed(() =>
  props.product?.category === 'server' &&
  [props.product?.server_cpu, props.product?.server_ram, props.product?.server_nvme].some(Boolean)
)

const lottieLogo = {"v":"4.8.0","meta":{"g":"LottieFiles AE 3.5.1","a":"","k":"","d":"","tc":""},"fr":60,"ip":0,"op":85,"w":60,"h":60,"nm":"Comp 1","ddd":0,"assets":[],"layers":[{"ddd":0,"ind":1,"ty":4,"nm":"Shape Layer 4","sr":1,"ks":{"o":{"a":0,"k":100,"ix":11},"r":{"a":0,"k":0,"ix":10},"p":{"a":0,"k":[30,30,0],"ix":2},"a":{"a":0,"k":[0,0,0],"ix":1},"s":{"a":0,"k":[100,100,100],"ix":6}},"ao":0,"shapes":[{"ty":"gr","it":[{"ind":0,"ty":"sh","ix":1,"ks":{"a":0,"k":{"i":[[0,0],[0,0]],"o":[[0,0],[0,0]],"v":[[11.25,9.75],[20.75,-10.25]],"c":false},"ix":2},"nm":"Path 1","mn":"ADBE Vector Shape - Group","hd":false},{"ty":"st","c":{"a":0,"k":[0.988235294118,0.176470588235,0.090196078431,1],"ix":3},"o":{"a":0,"k":100,"ix":4},"w":{"a":0,"k":10,"ix":5},"lc":2,"lj":2,"bm":0,"nm":"Stroke 1","mn":"ADBE Vector Graphic - Stroke","hd":false},{"ty":"tr","p":{"a":0,"k":[-0.875,0.125],"ix":2},"a":{"a":0,"k":[0,0],"ix":1},"s":{"a":0,"k":[100,100],"ix":3},"r":{"a":0,"k":0,"ix":6},"o":{"a":0,"k":100,"ix":7},"sk":{"a":0,"k":0,"ix":4},"sa":{"a":0,"k":0,"ix":5},"nm":"Transform"}],"nm":"Shape 1","np":3,"cix":2,"bm":0,"ix":1,"mn":"ADBE Vector Group","hd":false},{"ty":"tm","s":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":62,"s":[0]},{"t":65,"s":[100]}],"ix":1},"e":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":21,"s":[0]},{"t":30,"s":[100]}],"ix":2},"o":{"a":0,"k":0,"ix":3},"m":1,"ix":2,"nm":"Trim Paths 1","mn":"ADBE Vector Filter - Trim","hd":false}],"ip":0,"op":85,"st":0,"bm":0},{"ddd":0,"ind":2,"ty":4,"nm":"Shape Layer 3","sr":1,"ks":{"o":{"a":0,"k":100,"ix":11},"r":{"a":0,"k":0,"ix":10},"p":{"a":0,"k":[30,30,0],"ix":2},"a":{"a":0,"k":[0,0,0],"ix":1},"s":{"a":0,"k":[100,100,100],"ix":6}},"ao":0,"shapes":[{"ty":"gr","it":[{"ind":0,"ty":"sh","ix":1,"ks":{"a":0,"k":{"i":[[0,0],[0,0]],"o":[[0,0],[0,0]],"v":[[1.312,-10.188],[11.25,9.75]],"c":false},"ix":2},"nm":"Path 1","mn":"ADBE Vector Shape - Group","hd":false},{"ty":"st","c":{"a":0,"k":[0,0.41568627451,0.717647058824,1],"ix":3},"o":{"a":0,"k":100,"ix":4},"w":{"a":0,"k":10,"ix":5},"lc":2,"lj":2,"bm":0,"nm":"Stroke 1","mn":"ADBE Vector Graphic - Stroke","hd":false},{"ty":"tr","p":{"a":0,"k":[-0.875,0.125],"ix":2},"a":{"a":0,"k":[0,0],"ix":1},"s":{"a":0,"k":[100,100],"ix":3},"r":{"a":0,"k":0,"ix":6},"o":{"a":0,"k":100,"ix":7},"sk":{"a":0,"k":0,"ix":4},"sa":{"a":0,"k":0,"ix":5},"nm":"Transform"}],"nm":"Shape 1","np":3,"cix":2,"bm":0,"ix":1,"mn":"ADBE Vector Group","hd":false},{"ty":"tm","s":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":57,"s":[0]},{"t":62,"s":[100]}],"ix":1},"e":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":14,"s":[0]},{"t":21,"s":[100]}],"ix":2},"o":{"a":0,"k":0,"ix":3},"m":1,"ix":2,"nm":"Trim Paths 1","mn":"ADBE Vector Filter - Trim","hd":false}],"ip":0,"op":85,"st":0,"bm":0},{"ddd":0,"ind":3,"ty":4,"nm":"Shape Layer 2","sr":1,"ks":{"o":{"a":0,"k":100,"ix":11},"r":{"a":0,"k":0,"ix":10},"p":{"a":0,"k":[30,30,0],"ix":2},"a":{"a":0,"k":[0,0,0],"ix":1},"s":{"a":0,"k":[100,100,100],"ix":6}},"ao":0,"shapes":[{"ty":"gr","it":[{"ind":0,"ty":"sh","ix":1,"ks":{"a":0,"k":{"i":[[0,0],[0,0]],"o":[[0,0],[0,0]],"v":[[-8,10.375],[1.312,-10.188]],"c":false},"ix":2},"nm":"Path 1","mn":"ADBE Vector Shape - Group","hd":false},{"ty":"st","c":{"a":0,"k":[0,0.819607843137,1,1],"ix":3},"o":{"a":0,"k":100,"ix":4},"w":{"a":0,"k":10,"ix":5},"lc":2,"lj":2,"bm":0,"nm":"Stroke 1","mn":"ADBE Vector Graphic - Stroke","hd":false},{"ty":"tr","p":{"a":0,"k":[-0.875,0.125],"ix":2},"a":{"a":0,"k":[0,0],"ix":1},"s":{"a":0,"k":[100,100],"ix":3},"r":{"a":0,"k":0,"ix":6},"o":{"a":0,"k":100,"ix":7},"sk":{"a":0,"k":0,"ix":4},"sa":{"a":0,"k":0,"ix":5},"nm":"Transform"}],"nm":"Shape 1","np":3,"cix":2,"bm":0,"ix":1,"mn":"ADBE Vector Group","hd":false},{"ty":"tm","s":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":50,"s":[0]},{"t":57,"s":[100]}],"ix":1},"e":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":7,"s":[0]},{"t":14,"s":[100]}],"ix":2},"o":{"a":0,"k":0,"ix":3},"m":1,"ix":2,"nm":"Trim Paths 1","mn":"ADBE Vector Filter - Trim","hd":false}],"ip":0,"op":85,"st":0,"bm":0},{"ddd":0,"ind":4,"ty":4,"nm":"Shape Layer 1","sr":1,"ks":{"o":{"a":0,"k":100,"ix":11},"r":{"a":0,"k":0,"ix":10},"p":{"a":0,"k":[30,30,0],"ix":2},"a":{"a":0,"k":[0,0,0],"ix":1},"s":{"a":0,"k":[100,100,100],"ix":6}},"ao":0,"shapes":[{"ty":"gr","it":[{"ind":0,"ty":"sh","ix":1,"ks":{"a":0,"k":{"i":[[0,0],[0,0],[0,0],[0,0]],"o":[[0,0],[0,0],[0,0],[0,0]],"v":[[-18.375,-10.5],[-18.375,-10.5],[-18.375,-10.5],[-8,10.375]],"c":false},"ix":2},"nm":"Path 1","mn":"ADBE Vector Shape - Group","hd":false},{"ty":"st","c":{"a":0,"k":[0.29411765933,0.917647063732,0.984313726425,1],"ix":3},"o":{"a":0,"k":100,"ix":4},"w":{"a":0,"k":10,"ix":5},"lc":2,"lj":2,"bm":0,"nm":"Stroke 1","mn":"ADBE Vector Graphic - Stroke","hd":false},{"ty":"tr","p":{"a":0,"k":[-0.875,0.125],"ix":2},"a":{"a":0,"k":[0,0],"ix":1},"s":{"a":0,"k":[100,100],"ix":3},"r":{"a":0,"k":0,"ix":6},"o":{"a":0,"k":100,"ix":7},"sk":{"a":0,"k":0,"ix":4},"sa":{"a":0,"k":0,"ix":5},"nm":"Transform"}],"nm":"Shape 1","np":3,"cix":2,"bm":0,"ix":1,"mn":"ADBE Vector Group","hd":false},{"ty":"tm","s":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":40,"s":[0]},{"t":50,"s":[100]}],"ix":1},"e":{"a":1,"k":[{"i":{"x":[0.833],"y":[0.833]},"o":{"x":[0.167],"y":[0.167]},"t":0,"s":[0]},{"t":5,"s":[100]}],"ix":2},"o":{"a":0,"k":0,"ix":3},"m":1,"ix":2,"nm":"Trim Paths 1","mn":"ADBE Vector Filter - Trim","hd":false}],"ip":0,"op":85,"st":0,"bm":0}],"markers":[]}

const productDescription = computed(() => {
  // if(
  //   props.product?.description &&
  //   props.product?.category === 'server' &&
  //   props.product?.update_name &&
  //   !props.product?.update_name.includes(config.vmsNamePlaceholder)
  // ) {
  //   return `${props.product.update_name.replace('.virtual', '')} - ${props.product.description}`
  // }

  if(props.product?.description) {
    return props.product.description
  }

  return null
})
</script>

<template>
  <div v-if="!product.removed" class="b-checkout-order" :class="{'fade-out': product.removing }">
    <div class="b-checkout-order__header">
      <div
        class="b-checkout-order__header-title"
         :class="{
          'b-checkout-order__header-title--period': props.product?.supported_periods,
        }"
      >
        <h2 class="b-checkout-order__header-title-h2" v-html="product.name"></h2>
        <p v-if="productDescription" class="b-checkout-order__p" v-html="productDescription"></p>
      </div>
      <div class="b-checkout-order__options d-none d-xl-flex">
        <Periods
          v-if="isHandlingPeriods"
          :period="product.period"
          :product-id="product.id"
          :product="product"
        />
      </div>
      <div v-if="inHandlingParameters" class="b-checkout-order__options b-checkout-order__options--parameters">
        <ParameterBadge v-if="product?.server_cpu" :parameter="`${product.server_cpu}x`" suffix="CPU" />
        <ParameterBadge v-if="product?.server_ram" :parameter="product.server_ram" suffix="GB RAM" />
        <ParameterBadge v-if="product?.server_nvme" :parameter="product.server_nvme" suffix="GB SSD" />
      </div>
      <div class="b-checkout-order__price">
        <div class="b-checkout-order__price-inner">
          <div class="b-checkout-order__price-inner-upper">
            <span v-if="product.discount_percentage" class="b-checkout-order__price--old" :class="{'pulse': product.isUpdating}">{{ getPriceLabel(product.price_base) }}</span>
            <span class="b-checkout-order__price--main" :class="{'pulse': product.isUpdating}">{{ getPriceLabel(product.price_final) }}</span>
            <IconInfoBlack
              v-if="product.price_info"
              data-bs-toggle="tooltip" data-bs-placement="top"
              data-bs-custom-class="custom-tooltip"
              :data-bs-title="product.price_info"
            />
            <Button
              v-if="product.category !== 'virtual'"
              @click="openModal(`removeProductModal-${product.id}`)"
              variant="link"
              size="small"
              color="blue"
              class="button-remove p-0"
              :class="{
                'd-none d-xl-block': isHandlingPeriods || inHandlingParameters,
                'pe-none': $checkout.updatingBasket
              }"
              iconLeft='<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none"><path fill="#2C57DE" fill-rule="evenodd" d="M1.666 5c0-.46.373-.833.833-.833h15a.833.833 0 1 1 0 1.666h-15A.833.833 0 0 1 1.666 5Z" clip-rule="evenodd"/><path fill="#2C57DE" fill-rule="evenodd" d="M4.167 4.167c.46 0 .834.373.834.833v11.667c0 .145.08.36.276.556.196.196.411.277.557.277h8.333c.146 0 .361-.081.557-.277.195-.195.277-.41.277-.556V5a.833.833 0 1 1 1.666 0v11.667c0 .687-.335 1.305-.765 1.735-.429.43-1.047.765-1.735.765H5.834c-.688 0-1.306-.336-1.735-.765-.43-.43-.765-1.048-.765-1.735V5c0-.46.373-.833.833-.833Z" clip-rule="evenodd"/><path fill="#2C57DE" fill-rule="evenodd" d="M7.777 2.777c-.195.195-.276.41-.276.556V5a.833.833 0 1 1-1.667 0V3.333c0-.687.335-1.305.765-1.735.43-.43 1.047-.765 1.735-.765h3.333c.688 0 1.306.336 1.735.765.43.43.765 1.048.765 1.735V5a.833.833 0 1 1-1.666 0V3.333c0-.145-.082-.36-.277-.556-.196-.196-.411-.277-.557-.277H8.334c-.146 0-.361.081-.557.277ZM8.333 8.333c.46 0 .834.373.834.834v5a.833.833 0 0 1-1.667 0v-5c0-.46.373-.834.833-.834ZM11.667 8.333c.46 0 .834.373.834.834v5a.833.833 0 0 1-1.667 0v-5c0-.46.373-.834.833-.834Z" clip-rule="evenodd"/></svg>'
            />
            <MobileActionsDropdown
              v-if="isHandlingPeriods || inHandlingParameters"
              :product-id="props.product.id"
              :is-handling-periods="isHandlingPeriods"
            />
          </div>
          <div v-if="product.price_text_under" class="b-checkout-order__price-inner-lower">
            <span class="b-checkout-order__price--info" v-html="product.price_text_under"></span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="getItems.additionalProducts?.length || getItems.subProducts?.length" class="b-checkout-order__content b-checkout-order__content-main">
      <div v-if="getItems.additionalProducts?.length" class="b-checkout-order__service">
        <AdditionalProduct v-for="(subitem, index) in getItems.additionalProducts" :key="index" :product="subitem" />
      </div>
      <div v-if="getItems.subProducts" class="b-checkout-order__subservices" :class="{ 'b-checkout-order__subservices--no-additional-products-above': !getItems.additionalProducts.length }">
        <SubProduct v-for="subitem in getItems.subProducts" :key="product.id" :product="subitem" />
      </div>
    </div>
    <div v-if="product.isUpdatingChild" class="dottie-animation">
      <div class="overlay"></div>
      <DotLottieVue v-if="product.isAddingChild" :data="lottieLogo" class="dottie" autoplay loop />
    </div>
  </div>
   <Teleport to="body">
    <WarningModal
      v-if="props.product.remove_modal && props.product.in_basket"
      :id="`removeProductModal-${props.product.id}`"
      :title="props.product.remove_title"
      :description="props.product.remove_desc"
      :primary-button="$t('remove')"
      :secondary-button="$t('back')"
      @on-primary-action="removeProduct"
      severity="warning"
    />
    <Periods
      v-if="isHandlingPeriods"
      :period="product.period"
      :product-id="product.id"
      :use-mobile-version="true"
      :product="product"
    />
  </Teleport>
</template>

<style lang="scss" scoped>
.b-checkout-order {
  position: relative;
}
.dottie-animation {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 10;

  .overlay {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    opacity: 0.5;
    background: #FFF;
  }
  .dottie {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100px;
    height: 100px;
    transform: translate(-50%, -50%);
  }
}
</style>
